import { useState, useEffect } from 'react';
import { Clock, ExternalLink, FileText, Loader2, Download } from 'lucide-react';
import Header from '../components/layout/Header';
import DomainScanner from '../components/DomainScanner';
import RiskScoreGauge from '../components/RiskScoreGauge';
import BreachTable from '../components/BreachTable';
import { scanApi, reportApi } from '../services/api';
import type { ScanResult, ScanListItem } from '../types';

export default function ScanPage() {
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [recentScans, setRecentScans] = useState<ScanListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);

  useEffect(() => {
    loadRecentScans();
  }, []);

  const loadRecentScans = async () => {
    try {
      const response = await scanApi.listScans(10, 0);
      setRecentScans(response.scans);
    } catch (error) {
      console.error('Failed to load recent scans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScanComplete = (result: ScanResult) => {
    setScanResult(result);
    setReportUrl(null);
    setReportError(null);
    loadRecentScans(); // Refresh list
  };

  const handleGenerateReport = async () => {
    if (!scanResult) return;

    setGeneratingReport(true);
    setReportError(null);
    setReportUrl(null);

    try {
      const response = await reportApi.generate(scanResult.scanId);
      if (response.downloadUrl) {
        setReportUrl(response.downloadUrl);
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      setReportError(error instanceof Error ? error.message : 'Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const loadScan = async (scanId: string) => {
    try {
      const result = await scanApi.getScan(scanId);
      setScanResult(result);
      setReportUrl(null);
      setReportError(null);
    } catch (error) {
      console.error('Failed to load scan:', error);
    }
  };

  return (
    <>
      <Header title="Domain Scan" subtitle="Check any domain for credential exposure" />

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Scanner */}
        <div className="card">
          <h3 className="text-lg font-tech font-semibold mb-4">Scan a Domain</h3>
          <DomainScanner onScanComplete={handleScanComplete} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Scans */}
          <div className="card">
            <h3 className="text-lg font-tech font-semibold mb-4 flex items-center gap-2">
              <Clock size={20} className="text-teal" />
              Recent Scans
            </h3>
            {loading ? (
              <p className="text-gray-500 text-sm">Loading...</p>
            ) : recentScans.length === 0 ? (
              <p className="text-gray-500 text-sm">No scans yet</p>
            ) : (
              <div className="space-y-2">
                {recentScans.map((scan) => (
                  <button
                    key={scan.id}
                    onClick={() => loadScan(scan.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      scanResult?.scanId === scan.id
                        ? 'border-teal bg-teal/10'
                        : 'border-teal/20 hover:border-teal/40 hover:bg-teal/5'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm truncate">{scan.domain}</span>
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded"
                        style={{
                          backgroundColor:
                            scan.riskScore >= 75
                              ? 'rgba(255, 61, 0, 0.2)'
                              : scan.riskScore >= 50
                              ? 'rgba(255, 184, 0, 0.2)'
                              : scan.riskScore >= 25
                              ? 'rgba(255, 193, 7, 0.2)'
                              : 'rgba(0, 200, 83, 0.2)',
                          color:
                            scan.riskScore >= 75
                              ? '#FF3D00'
                              : scan.riskScore >= 50
                              ? '#FFB800'
                              : scan.riskScore >= 25
                              ? '#FFC107'
                              : '#00C853',
                        }}
                      >
                        {scan.riskScore}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {scan.breachCount} breaches •{' '}
                      {new Date(scan.createdAt).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Scan Result */}
          <div className="lg:col-span-2 card">
            {scanResult ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-tech font-semibold flex items-center gap-2">
                      {scanResult.domain}
                      <ExternalLink size={16} className="text-gray-500" />
                    </h3>
                    <p className="text-sm text-gray-500">
                      {scanResult.breachCount} exposed credentials found
                    </p>
                  </div>
                  <RiskScoreGauge riskScore={scanResult.riskScore} size="sm" />
                </div>

                {/* Report Generation */}
                <div className="mb-6 p-4 bg-teal/5 border border-teal/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="text-teal" size={24} />
                      <div>
                        <p className="font-medium">Generate PDF Report</p>
                        <p className="text-sm text-gray-500">
                          Create a branded report for your client
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {reportUrl ? (
                        <a
                          href={reportUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary flex items-center gap-2"
                        >
                          <Download size={16} />
                          Download PDF
                        </a>
                      ) : (
                        <button
                          onClick={handleGenerateReport}
                          disabled={generatingReport}
                          className="btn-primary flex items-center gap-2 disabled:opacity-50"
                        >
                          {generatingReport ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <FileText size={16} />
                              Generate Report
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  {reportError && (
                    <p className="mt-2 text-sm text-signal-red">{reportError}</p>
                  )}
                </div>

                <BreachTable breaches={scanResult.breaches} />
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>Select a scan or enter a new domain to see results</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
